export type LoanSource = 'LEVERAGED_LOAN' | 'PRIVATE_CREDIT';

/** The private credit home (features/privateCredit): board, register and work queue in one place. */
export const loanListPath = (source: LoanSource): string =>
  source === 'PRIVATE_CREDIT'
    ? '/portfolio-management?tab=alternatives&sub=private-credit' // F-PCP-019: back into the hub, like an ABF facility
    : '/portfolio-management?tab=credit-loans&sub=loans';

/**
 * Loan 360: the loan's own page, like an ABF facility's. It reads only this loan's records, so a link opens it
 * directly. The tab is a query parameter, not a path segment: the app remounts a page whenever its path
 * changes, which would make every tab click reload the whole page.
 */
export const loan360Path = (
  source: LoanSource | string,
  loanId: number | string,
  tab = 'overview',
): string => {
  const base = `/portfolio-management/credit/loans/${encodeURIComponent(String(source).toUpperCase())}/${encodeURIComponent(String(loanId))}`;
  return !tab || tab === 'overview' ? base : `${base}?tab=${encodeURIComponent(tab)}`;
};

/** The agreement review: every extracted term beside its source in the document. */
export const agreementReviewPath = (jobId: string): string =>
  `/portfolio-management/credit/agreements/${encodeURIComponent(jobId)}`;

export const securityMasterPath = (cusip: string): string =>
  `/reference-data?${new URLSearchParams({ tab: 'securities', cusip }).toString()}`;

export const creditLoanReferencePath = (query?: string): string => {
  const params = new URLSearchParams({ tab: 'credit-loans' });
  if (query) params.set('query', query);
  return `/reference-data?${params.toString()}`;
};

export const iborPositionPath = (query: string): string =>
  `/ibor?${new URLSearchParams({ query }).toString()}`;

export const blotterLoanPath = (query: string): string =>
  `/bond-transactions?${new URLSearchParams({ query }).toString()}`;

export interface LeveragedLoanTicketPrefill {
  securityId?: number | string | null;
  cusip?: string | null;
  securityName?: string | null;
  loanId?: number | string | null;
  loanName?: string | null;
  borrowerName?: string | null;
  facilityType?: string | null;
}

export const leveragedLoanTicketPath = (cusip?: string): string => {
  const params = new URLSearchParams();
  if (cusip) params.set('cusip', cusip);
  const suffix = params.toString();
  return `/trade-entry/leveraged-loan${suffix ? `?${suffix}` : ''}`;
};

export const privateCreditTicketPath = (cusip?: string): string => {
  const params = new URLSearchParams();
  if (cusip) params.set('cusip', cusip);
  const suffix = params.toString();
  return `/trade-entry/private-fixed-income${suffix ? `?${suffix}` : ''}`;
};

export const leveragedLoanTicketState = (prefill: LeveragedLoanTicketPrefill) => {
  const securityId = prefill.securityId != null ? Number(prefill.securityId) : undefined;
  const cusip = prefill.cusip || undefined;
  const securityName = prefill.securityName || prefill.loanName || prefill.borrowerName || cusip || undefined;

  return {
    state: {
      ticketPrefill: {
        securityId,
        cusip,
        identifier: cusip || '',
        security: securityName || '',
        loanId: prefill.loanId,
        facilityType: prefill.facilityType || '',
      },
      pickedSecurity: securityId
        ? {
            id: securityId,
            cusip,
            securityName,
            loanId: prefill.loanId,
            facilityType: prefill.facilityType,
          }
        : undefined,
    },
  };
};
